import { Box } from '@mui/material'

import React, { useEffect, useRef } from 'react'

import type { MenuItem } from '../../domain/menu-item'
import type { MenuVendor } from '../../domain/menu-vendor'
import { MemoizedMenuItemCard, MemoizedMenuItemCardOptimized } from '../menu-item-card'

const GRID_GAP_PX = 32
const ROW_SPACING_PX = 16
const LAST_ROW_SPACING_PX = 32
// Estimated height for contain-intrinsic-size (helps browser reserve space)
const ESTIMATED_ROW_HEIGHT_PX = 196

type Props = {
  vendor: MenuVendor
  taxAcronym: string
  cardCols: number
  loadingMore: boolean
  onLoadMore?: () => void
  hasMore?: boolean
  menuItems: MenuItem[]
  useOptimizedCard?: boolean
}

type MenuRowProps = {
  items: MenuItem[]
  isLastRow: boolean
  cardCols: number
  vendor: MenuVendor
  taxAcronym: string
  rowIndex: number
  useOptimizedCard?: boolean
}

// Memoized row component with content-visibility
const MenuRow = React.memo(
  ({ items, isLastRow, cardCols, vendor, taxAcronym, useOptimizedCard = false }: MenuRowProps) => {
    const spacing = isLastRow ? LAST_ROW_SPACING_PX : ROW_SPACING_PX
    const CardComponent = useOptimizedCard ? MemoizedMenuItemCardOptimized : MemoizedMenuItemCard

    return (
      <Box
        sx={{
          pb: `${spacing}px`,
          // CSS content-visibility for native browser optimization
          contentVisibility: 'auto',
          // Reserve space when content is not rendered
          containIntrinsicSize: `auto ${ESTIMATED_ROW_HEIGHT_PX}px`,
        }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `repeat(${cardCols}, minmax(0, 1fr))`,
            gap: `${GRID_GAP_PX}px`,
          }}>
          {items.map((item) => (
            <CardComponent
              key={item._id}
              numberInCol={cardCols}
              taxAcronym={taxAcronym}
              item={item}
              vendor={vendor}
            />
          ))}
        </Box>
      </Box>
    )
  },
  (prevProps, nextProps) => {
    // Only re-render if items actually changed
    if (prevProps.items.length !== nextProps.items.length) return false
    if (prevProps.cardCols !== nextProps.cardCols) return false
    if (prevProps.isLastRow !== nextProps.isLastRow) return false
    if (prevProps.vendor.id !== nextProps.vendor.id) return false
    if (prevProps.useOptimizedCard !== nextProps.useOptimizedCard) return false

    // Check if item IDs changed
    for (let i = 0; i < prevProps.items.length; i++) {
      if (prevProps.items[i]._id !== nextProps.items[i]._id) return false
    }
    return true
  },
)

MenuRow.displayName = 'MenuRow'

export const ContentVisibilityRenderer: React.FC<Props> = ({
  vendor,
  menuItems,
  taxAcronym,
  cardCols,
  loadingMore,
  onLoadMore,
  hasMore,
  useOptimizedCard = false,
}) => {
  const sentinelRef = useRef<HTMLDivElement | null>(null)

  // Group items into rows
  const rows = React.useMemo(() => {
    const result: { items: MenuItem[]; key: string }[] = []
    for (let i = 0; i < menuItems.length; i += cardCols) {
      const rowItems = menuItems.slice(i, i + cardCols)
      const key = rowItems.map((item) => item._id).join('|') || `row-${i}`
      result.push({ items: rowItems, key })
    }
    return result
  }, [menuItems, cardCols])

  // Intersection observer for infinite loading
  useEffect(() => {
    if (!hasMore || !onLoadMore) return
    const node = sentinelRef.current
    if (!node) return

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !loadingMore) {
            void onLoadMore()
          }
        })
      },
      { rootMargin: '400px' },
    )

    observer.observe(node)
    return () => {
      observer.disconnect()
    }
  }, [hasMore, onLoadMore, loadingMore])

  if (menuItems.length === 0) {
    return null
  }

  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      {rows.map((row, index) => (
        <MenuRow
          key={row.key}
          items={row.items}
          isLastRow={index === rows.length - 1}
          cardCols={cardCols}
          vendor={vendor}
          taxAcronym={taxAcronym}
          rowIndex={index}
          useOptimizedCard={useOptimizedCard}
        />
      ))}

      {/* Sentinel for infinite scroll */}
      {hasMore && <Box ref={sentinelRef} sx={{ height: 4 }} aria-hidden="true" />}
    </Box>
  )
}

export default ContentVisibilityRenderer
